import React, { useState, useEffect } from 'react';
import api from '../lib/axios';
import Header from '../components/Header';

const AdminBlogCategorias = () => {
  const [categorias, setCategorias] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Form state
  const [formData, setFormData] = useState({
    nome: '',
    descricao: '',
    cor: '#FF8C42'
  });

  // Edit state
  const [editingId, setEditingId] = useState(null);
  const [editData, setEditData] = useState({});

  // Fetch categorias on mount
  useEffect(() => {
    fetchCategorias();
  }, []);

  const fetchCategorias = async () => {
    try {
      setLoading(true);
      const { data } = await api.get('/api/blog/categorias');
      setCategorias(data);
      setError('');
    } catch (err) {
      setError('Erro ao carregar categorias');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleAddCategoria = async (e) => {
    e.preventDefault();

    if (!formData.nome.trim()) {
      setError('Nome da categoria é obrigatório');
      return;
    }

    try {
      setLoading(true);
      await api.post('/api/blog/categorias', {
        nome: formData.nome,
        descricao: formData.descricao,
        cor: formData.cor
      });

      setSuccess('Categoria adicionada com sucesso!');
      setFormData({ nome: '', descricao: '', cor: '#FF8C42' });
      setError('');

      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(''), 3000);

      // Refresh list
      await fetchCategorias();
    } catch (err) {
      setError('Erro ao adicionar categoria');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleStartEdit = (categoria) => {
    setEditingId(categoria.id);
    setEditData({
      id: categoria.id,
      nome: categoria.nome,
      descricao: categoria.descricao,
      cor: categoria.cor
    });
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditData({});
  };

  const handleSaveEdit = async (id) => {
    if (!editData.nome.trim()) {
      setError('Nome da categoria é obrigatório');
      return;
    }

    try {
      setLoading(true);
      await api.put('/api/blog/categorias', {
        id: editData.id,
        nome: editData.nome,
        descricao: editData.descricao,
        cor: editData.cor
      });

      setSuccess('Categoria atualizada com sucesso!');
      setError('');
      setEditingId(null);
      setEditData({});

      setTimeout(() => setSuccess(''), 3000);
      await fetchCategorias();
    } catch (err) {
      setError('Erro ao atualizar categoria');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCategoria = async (id) => {
    if (!window.confirm('Tem certeza que deseja deletar esta categoria?')) {
      return;
    }

    try {
      setLoading(true);
      await api.delete('/api/blog/categorias', {
        data: { id }
      });

      setSuccess('Categoria deletada com sucesso!');
      setError('');

      setTimeout(() => setSuccess(''), 3000);
      await fetchCategorias();
    } catch (err) {
      setError('Erro ao deletar categoria');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header
        title="Categorias do Blog"
        subtitle="Gerencie as categorias de conteúdo do seu blog"
      />

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Alert Messages */}
        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 text-red-700 rounded-2xl">
            {error}
          </div>
        )}
        {success && (
          <div className="mb-4 p-4 bg-green-50 border border-green-200 text-green-700 rounded-2xl">
            {success}
          </div>
        )}

        {/* Add Category Form */}
        <div className="bg-white rounded-2xl shadow-sm p-6 mb-8">
          <h2 className="text-xl font-bold text-gray-900 mb-6">Adicionar Nova Categoria</h2>
          <form onSubmit={handleAddCategoria} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Nome Input */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nome
                </label>
                <input
                  type="text"
                  name="nome"
                  value={formData.nome}
                  onChange={handleFormChange}
                  placeholder="Ex: Tecnologia"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent outline-none"
                />
              </div>

              {/* Descricao Input */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Descrição
                </label>
                <input
                  type="text"
                  name="descricao"
                  value={formData.descricao}
                  onChange={handleFormChange}
                  placeholder="Ex: Artigos sobre tecnologia"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent outline-none"
                />
              </div>

              {/* Cor Input */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Cor
                </label>
                <input
                  type="color"
                  name="cor"
                  value={formData.cor}
                  onChange={handleFormChange}
                  className="w-full h-10 border border-gray-300 rounded-lg cursor-pointer"
                />
              </div>
            </div>

            {/* Adicionar Button */}
            <div className="flex justify-end pt-4">
              <button
                type="submit"
                disabled={loading}
                className="px-6 py-2 bg-orange-500 hover:bg-orange-600 disabled:bg-gray-400 text-white font-semibold rounded-lg transition-colors"
              >
                {loading ? 'Adicionando...' : 'Adicionar'}
              </button>
            </div>
          </form>
        </div>

        {/* Categories Table */}
        <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">
                    Nome
                  </th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">
                    Slug
                  </th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">
                    Descrição
                  </th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">
                    Artigos
                  </th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">
                    Ações
                  </th>
                </tr>
              </thead>
              <tbody>
                {categorias.length === 0 ? (
                  <tr>
                    <td colSpan="5" className="px-6 py-8 text-center text-gray-500">
                      {loading ? 'Carregando...' : 'Nenhuma categoria encontrada'}
                    </td>
                  </tr>
                ) : (
                  categorias.map(categoria => (
                    <tr key={categoria.id} className="border-b border-gray-200 hover:bg-gray-50 transition-colors">
                      {/* Nome com cor */}
                      {editingId === categoria.id ? (
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-3">
                            <input
                              type="color"
                              name="cor"
                              value={editData.cor}
                              onChange={handleEditChange}
                              className="w-8 h-8 border border-gray-300 rounded cursor-pointer"
                            />
                            <input
                              type="text"
                              name="nome"
                              value={editData.nome}
                              onChange={handleEditChange}
                              className="flex-1 px-3 py-1 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent outline-none"
                            />
                          </div>
                        </td>
                      ) : (
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-3">
                            <div
                              className="w-4 h-4 rounded-full border border-gray-200"
                              style={{ backgroundColor: categoria.cor }}
                            />
                            <span className="font-medium text-gray-900">{categoria.nome}</span>
                          </div>
                        </td>
                      )}

                      {/* Slug */}
                      <td className="px-6 py-4 text-sm text-gray-600">
                        {categoria.slug}
                      </td>

                      {/* Descricao */}
                      {editingId === categoria.id ? (
                        <td className="px-6 py-4">
                          <input
                            type="text"
                            name="descricao"
                            value={editData.descricao}
                            onChange={handleEditChange}
                            className="w-full px-3 py-1 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent outline-none"
                          />
                        </td>
                      ) : (
                        <td className="px-6 py-4 text-sm text-gray-600">
                          {categoria.descricao}
                        </td>
                      )}

                      {/* Artigos Count */}
                      <td className="px-6 py-4 text-sm text-gray-600">
                        {categoria.artigos || 0}
                      </td>

                      {/* Acoes */}
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          {editingId === categoria.id ? (
                            <>
                              <button
                                onClick={() => handleSaveEdit(categoria.id)}
                                disabled={loading}
                                className="px-3 py-1 bg-green-500 hover:bg-green-600 disabled:bg-gray-400 text-white text-sm font-medium rounded transition-colors"
                              >
                                Salvar
                              </button>
                              <button
                                onClick={handleCancelEdit}
                                disabled={loading}
                                className="px-3 py-1 bg-gray-400 hover:bg-gray-500 disabled:bg-gray-400 text-white text-sm font-medium rounded transition-colors"
                              >
                                Cancelar
                              </button>
                            </>
                          ) : (
                            <>
                              <button
                                onClick={() => handleStartEdit(categoria)}
                                className="px-3 py-1 bg-blue-500 hover:bg-blue-600 text-white text-sm font-medium rounded transition-colors"
                              >
                                Editar
                              </button>
                              <button
                                onClick={() => handleDeleteCategoria(categoria.id)}
                                className="px-3 py-1 bg-red-500 hover:bg-red-600 text-white text-sm font-medium rounded transition-colors"
                              >
                                Deletar
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminBlogCategorias;
